import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase';
import { collection, onSnapshot, query, orderBy, doc, updateDoc, increment, setDoc } from 'firebase/firestore';
import { NoticeModel, MemberModel } from '../../../../models';
import { Bell, Search, MessageSquare } from 'lucide-react';
import StudentNoticeCard from './StudentNoticeCard';
import StudentNoticeDetailView from './StudentNoticeDetailView';
import Header from '../studentFrame/header';

interface StudentNoticeProps {
    member: MemberModel;
}

const StudentNotice: React.FC<StudentNoticeProps> = ({ member }) => {
    const [notices, setNotices] = useState<NoticeModel[]>([]);
    const [selectedNotice, setSelectedNotice] = useState<NoticeModel | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const q = query(
            collection(db, 'notices'),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list: NoticeModel[] = [];
            snapshot.forEach((doc) => {
                const data = doc.data() as NoticeModel;
                const matchesAudience = data.targetAudience.includes('all') || data.targetAudience.includes('student');
                const matchesBranch = !data.branchId || data.branchId === 'all' || data.branchId === member.branchId;

                if (matchesAudience && matchesBranch) {
                    list.push({ id: doc.id, ...data } as NoticeModel);
                }
            });
            setNotices(list);
            setIsLoading(false);
        }, (err) => {
            console.error("Error fetching notices:", err);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleViewDetail = async (notice: NoticeModel) => {
        setSelectedNotice(notice);
        // Increment view count
        try {
            await updateDoc(doc(db, 'notices', notice.id), {
                viewCount: increment(1)
            });

            // Update user read status (subcollection)
            const readRef = doc(db, 'members', member.id, 'read_notices', notice.id);
            // Use setDoc with merge:true to be safe, though just writing readAt is enough
            await setDoc(readRef, { readAt: Date.now() }, { merge: true });
        } catch (error) {
            console.error('Error updating notice/member status:', error);
        }
    };

    const filteredNotices = notices
        .filter(n => n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            n.content.toLowerCase().includes(searchTerm.toLowerCase()));

    if (selectedNotice) {
        return (
            <StudentNoticeDetailView
                notice={selectedNotice}
                onBack={() => setSelectedNotice(null)}
            />
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Header Section - Consistent Style */}
            {/* Header Section - Consistent Style */}
            <Header
                icon={Bell}
                title="공지사항"
                subtitle="학습 중요 공지 및 새로운 소식을 확인하세요."
            />

            {/* Search Bar */}
            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <input
                    type="text"
                    placeholder="공지사항 제목이나 내용으로 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-white border border-slate-100 rounded-xl pl-11 pr-4 py-3 text-sm font-medium shadow-sm focus:ring-2 focus:ring-indigo-100 outline-none transition-all placeholder:text-slate-300"
                />
            </div>

            {/* Notice List */}
            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                    <p className="text-slate-400 font-bold">공지사항을 불러오는 중입니다...</p>
                </div>
            ) : filteredNotices.length > 0 ? (
                <div className="space-y-3">
                    {filteredNotices.map((notice) => (
                        <StudentNoticeCard
                            key={notice.id}
                            notice={notice}
                            onView={() => handleViewDetail(notice)}
                        />
                    ))}
                </div>
            ) : (
                <div className="py-24 text-center bg-white rounded-[4rem] border border-slate-100 shadow-sm border-dashed">
                    <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 text-slate-200">
                        <Bell size={40} />
                    </div>
                    <h3 className="text-xl font-black text-slate-800">게시된 공지사항이 없습니다</h3>
                    <p className="text-sm font-bold text-slate-400 mt-2">새로운 소식이 올라오면 여기서 확인하실 수 있습니다.</p>
                </div>
            )}
        </div>
    );
};

export default StudentNotice;
